import { Col, Row } from "react-grid-system";
import { DebtDisclosure as DebtDisclosureType } from "../../lib/api/models";
import Container from "../common/Container";
import toolStyles from "../../sharedStyles/Tool.module.css";
import styles from "./DebtDisclosure.module.css";
import StyledButton from "../common/buttons/StyledButton";
import StyledRadioButton from "../common/inputs/StyledRadioButton";
import { useState } from "react";
import ConversationScript from "../common/ConversationScript";
import getDebtDisclosureScriptDetails from "./getDebtDisclosureScriptDetails";
import DebtDisclosureOnCloseWarningModal from "../DebtDisclosureOnCloseWarningModal";
import classNames from "classnames";
import { useAddNote } from "../../lib/api/queries";
import { useChannelState } from "../../lib/channelContext";
import { ChannelMapToFull } from "../../lib/channelUtil";

const strings = {
  title: "Debt Disclosure Account",
  submit: "Submit",
  close: "Close",
  disclosureRead: "Disclosure Read?",
  yes: "Yes",
  no: "No",
};

function getDebtDisclosureNote({
  stateCode,
  disclosure,
  readDebtDisclosure,
}: {
  stateCode?: string;
  disclosure?: DebtDisclosureType;
  readDebtDisclosure: boolean;
}) {
  return `${disclosure?.code ? `DEBT DISCLOSURE ${disclosure.code}, ` : ""}READ ${readDebtDisclosure ? "Yes" : "No"}${stateCode ? `, STATE ${stateCode}` : ""}`;
}

type Props = {
  toolId: string;
  dwbuid: string;
  referenceId?: string;
  disabled: boolean;
  stateCode?: string;
  disclosure?: DebtDisclosureType;
  onCloseTool: (id: string) => void;
  onSubmit: () => void;
  readDebtDisclosure: boolean | undefined;
  setReadDebtDisclosure: React.Dispatch<
    React.SetStateAction<boolean | undefined>
  >;
  onSetToolInProgress: (id: string, inProgress: boolean) => void;
  onDisplayGeneralErrors: (error: unknown) => void;
};

export default function DebtDisclosure(props: Props) {
  const { channel } = useChannelState();

  const { mutateAsync: addNote, isLoading: isAddNoteLoading } = useAddNote();

  const [value, setValue] = useState<boolean | undefined>(
    props.readDebtDisclosure,
  );

  const [showClickSubmitModal, setShowClickSubmitModal] = useState(false);

  if (!props.disclosure) {
    return null;
  }

  const scriptDetails = getDebtDisclosureScriptDetails(props.disclosure);

  return (
    <>
      <div className={toolStyles.container} id={props.toolId}>
        <Container>
          <Row
            className={classNames(
              toolStyles.header,
              toolStyles.headerWithDivider,
            )}
          >
            <Col md={9} className={toolStyles.centerCol}>
              <h2 className={toolStyles.title}>{strings.title}</h2>
            </Col>
            <Col md={2}>
              <StyledButton
                className={styles.headerButton}
                isLoading={isAddNoteLoading}
                disabled={value === undefined || props.disabled}
                onClick={async () => {
                  if (value === undefined) {
                    return;
                  }
                  try {
                    await addNote({
                      dwbuid: props.dwbuid,
                      channel: ChannelMapToFull[channel],
                      content: getDebtDisclosureNote({
                        disclosure: props.disclosure,
                        readDebtDisclosure: value,
                        stateCode: props.stateCode,
                      }),
                      priority: "REGULAR",
                      referenceId: props.referenceId,
                    });
                    props.setReadDebtDisclosure(value);
                    props.onSubmit();
                  } catch (err) {
                    props.onDisplayGeneralErrors(err);
                  }
                }}
              >
                {strings.submit}
              </StyledButton>
            </Col>
            <Col md={1}>
              <StyledButton
                variant="secondary"
                disabled={isAddNoteLoading}
                className={styles.headerButton}
                onClick={() => {
                  if (
                    value === undefined ||
                    props.readDebtDisclosure === value
                  ) {
                    props.onCloseTool(props.toolId);
                  } else {
                    setShowClickSubmitModal(true);
                  }
                }}
              >
                {strings.close}
              </StyledButton>
            </Col>
          </Row>
          <Row>
            <Col md={4}>
              <div className={styles.divider} />
              <div className={styles.radioContainer}>
                <div className={styles.radioLabel}>
                  {strings.disclosureRead}
                </div>
                <StyledRadioButton
                  disabled={props.disabled || isAddNoteLoading}
                  className={styles.radioButton}
                  checked={value === true}
                  label={strings.yes}
                  onChange={() => {
                    setValue(true);
                    props.onSetToolInProgress(
                      props.toolId,
                      props.readDebtDisclosure !== true,
                    );
                  }}
                />
                <StyledRadioButton
                  disabled={props.disabled || isAddNoteLoading}
                  className={styles.radioButton}
                  checked={value === false}
                  label={strings.no}
                  onChange={() => {
                    setValue(false);
                    props.onSetToolInProgress(
                      props.toolId,
                      props.readDebtDisclosure !== false,
                    );
                  }}
                />
              </div>
              <div className={styles.divider} />
            </Col>
            <Col md={8}>
              <ConversationScript
                scripts={scriptDetails.script}
                scriptName={props.disclosure.script ?? ""}
              />
            </Col>
          </Row>
        </Container>
      </div>
      <DebtDisclosureOnCloseWarningModal
        isOpen={showClickSubmitModal}
        onRequestClose={() => setShowClickSubmitModal(false)}
      />
    </>
  );
}
